import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
// FIX: Added ../ to properly navigate out of the pages folder
import { db, auth, functions } from '../firebase';
import { collection, onSnapshot, query, doc, getDoc, setDoc, updateDoc, where, getDocs, documentId, orderBy, limit } from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
// FIX: Added ../ to navigate to constants and assets
import { ipfsGateway, FIREBASE_PROJECT_ID, API_BASE_URL, BASE_MAX_STAMINA, TOKEN_CONTRACT, TOKENS, tokenContracts, tokenDecimals, buildingConfigClient, ATOMIC_API_ENDPOINTS, itemTemplates, EMBLEM_NAMES, MASTER_ACCOUNT_WAX_ID, ADMIN_ACCOUNTS, STAKING_TIERS, VAULT_TIERS, VAULT_TOKEN_INFO, VAULT_TOKEN_VALUE, getUpgradeCostClient, Client, calculateDiminishedBoost, calculateMaxHp, calculateXpForNextLevel } from '../constants/gameConfig';
import characterImage from '../assets/firecat.png';
import placeholderImage from '../assets/equip.png';

function VaultPage({
  playerData,
  tokenBalances,
  vaultStakes,
  vaultTotalValueLocked,
  vaultUserBoosts,
  selectedVaultToken,
  setSelectedVaultToken,
  vaultStakeAmountInput,
  setVaultStakeAmountInput,
  vaultLockPeriodInput,
  setVaultLockPeriodInput,
  vaultStakingFeedback,
  setVaultStakingFeedback,
  isInitiatingVaultStake,
  handleInitiateVaultStake,
  handleClaimVaultRewards,
  handleUnstakeVaultTokens,
  STAKING_TIERS,
  VAULT_TIERS,
  VAULT_TOKEN_INFO,
  MASTER_ACCOUNT_WAX_ID,
  handleBurnAprEmblem,
  handleClaimAllVaultRewards,
  handleUnstakeAll
}) {
  const [isClaimingAll, setIsClaimingAll] = useState(false);
  const [isUnstakingAll, setIsUnstakingAll] = useState(false);
  const [claimCooldown, setClaimCooldown] = useState("");

  const HALVING_SCHEDULE = [
    { date: "2026-01-01", multiplier: 0.5 },
    { date: "2027-01-01", multiplier: 0.25 },
    { date: "2028-01-01", multiplier: 0.125 }
  ];

  const getEffectiveApr = (baseApr) => {
    const nowMillis = Date.now();
    let currentMultiplier = 1.0;
    for (const phase of HALVING_SCHEDULE) {
      const phaseTime = new Date(phase.date + "T00:00:00Z").getTime();
      if (nowMillis >= phaseTime) {
        currentMultiplier = phase.multiplier;
      }
    }
    return baseApr * currentMultiplier;
  };

  useEffect(() => {
    const updateTimer = () => {
      if (!playerData?.lastVaultClaimTimestamp) {
        setClaimCooldown("");
        return;
      }
      const lastClaimTime = playerData.lastVaultClaimTimestamp.toDate().getTime();
      const now = Date.now();
      const sixHoursMillis = 6 * 60 * 60 * 1000;
      const nextClaimTime = lastClaimTime + sixHoursMillis;

      if (now >= nextClaimTime) {
        setClaimCooldown("");
      } else {
        const diff = nextClaimTime - now;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setClaimCooldown(`${hours}h ${minutes}m ${seconds}s`);
      }
    };

    updateTimer();
    const intervalId = setInterval(updateTimer, 1000);
    return () => clearInterval(intervalId);
  }, [playerData?.lastVaultClaimTimestamp]);

  const totalBoosts = useMemo(() => {
    let boost = 0;
    if (playerData?.vault_summary && playerData.vault_summary[selectedVaultToken]) {
      boost = playerData.vault_summary[selectedVaultToken].activeBoost;
    }
    return boost;
  }, [playerData, selectedVaultToken]);

  const handleSelectToken = (token) => {
    setSelectedVaultToken(token);
    setVaultStakingFeedback('');
    setVaultStakeAmountInput('');
  };

  const handleClaimAll = async () => {
    if (playerData?.isBlacklisted) return;
    const hasRewards = vaultStakes && vaultStakes.some(s => (s.accruedRewards || 0) > 0.00001);
    if (!hasRewards) {
      setVaultStakingFeedback("No accrued rewards detected yet. Let them build up!");
      return;
    }
    if (!window.confirm("This will claim ALL pending rewards from ALL your staked tokens via the smart contract. Continue?")) return;

    setIsClaimingAll(true);
    await handleClaimAllVaultRewards();
    setIsClaimingAll(false);
  };

  const isCooldownActive = claimCooldown !== "";
  const selectedTier = STAKING_TIERS[vaultLockPeriodInput];
  const currentEffectiveApr = selectedTier ? getEffectiveApr(selectedTier.apr) : 0;

  // Formatting helpers
  const formatTVL = () => {
    let total = 0;
    if (vaultTotalValueLocked) {
      Object.keys(vaultTotalValueLocked).forEach(key => {
        total += vaultTotalValueLocked[key];
      });
    }
    return total.toLocaleString(undefined, { maximumFractionDigits: 0 });
  };

  // Check if Burn Boost is active
  const now = Date.now();
  const burnExpires = vaultUserBoosts?.burn_boost_expires || 0;
  const isBurnActive = burnExpires > now;
  const gangBoostPct = (vaultUserBoosts?.gang_boost_scaled || 0) / 100;

  const [burnTimer, setBurnTimer] = useState("");
  useEffect(() => {
    if (!isBurnActive) return;
    const interval = setInterval(() => {
      const diff = burnExpires - Date.now();
      if (diff <= 0) {
        setBurnTimer("");
      } else {
        const h = Math.floor(diff / (1000 * 60 * 60));
        const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const s = Math.floor((diff % (1000 * 60)) / 1000);
        setBurnTimer(`${h}h ${m}m ${s}s`);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [burnExpires, isBurnActive]);

  return (
    <div className="w-full max-w-5xl mx-auto p-4 font-sans text-slate-200">

      {/* HEADER DASHBOARD */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">

        {/* TVL */}
        <div className="bg-black/60 backdrop-blur-md border border-zinc-700 rounded-xl p-6 shadow-[0_0_15px_rgba(34,197,94,0.15)]">
          <h3 className="text-zinc-400 text-sm font-bold uppercase tracking-wider mb-2">Protocol TVL</h3>
          <div className="text-3xl font-extrabold text-green-400 drop-shadow-[0_0_8px_rgba(74,222,128,0.8)] flex items-baseline gap-2">
            {formatTVL()} <span className="text-lg text-green-600 font-bold">TOKENS</span>
          </div>
          <p className="text-xs text-zinc-500 mt-2">Total Value Locked on-chain</p>
        </div>

        {/* MULTIPLIERS */}
        <div className="md:col-span-2 bg-black/60 backdrop-blur-md border border-zinc-700 rounded-xl p-6 shadow-[0_0_15px_rgba(239,68,68,0.15)] flex flex-col justify-center">
          <h3 className="text-zinc-400 text-sm font-bold uppercase tracking-wider mb-4">Your Active Multipliers</h3>
          <div className="flex flex-wrap gap-6 items-center">
            <div className="flex flex-col">
              <span className="text-xs text-zinc-500 uppercase">Base APR</span>
              <span className="text-xl font-bold text-white">Halving (0.5x)</span>
            </div>
            <div className="h-8 w-px bg-zinc-700 hidden sm:block"></div>
            <div className="flex flex-col">
              <span className="text-xs text-zinc-500 uppercase">Gang Boost</span>
              <span className="text-xl font-bold text-blue-400">+{gangBoostPct}%</span>
            </div>
            <div className="h-8 w-px bg-zinc-700 hidden sm:block"></div>
            <div className="flex flex-col">
              <span className="text-xs text-red-500 uppercase flex items-center gap-1">
                🔥 Burn Boost
              </span>
              {isBurnActive ? (
                <div className="text-xl font-bold text-red-500 drop-shadow-[0_0_5px_rgba(239,68,68,0.8)]">
                  +0.25% <span className="text-sm font-normal text-red-300">({burnTimer})</span>
                </div>
              ) : (
                <div className="text-xl font-bold text-zinc-600">Inactive</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* WATCHTOWER FURNACE */}
      <div className="bg-gradient-to-r from-red-950/40 to-black/60 backdrop-blur-md border border-red-900/50 rounded-xl p-6 mb-8 flex flex-col md:flex-row items-center justify-between gap-4 shadow-[0_0_20px_rgba(220,38,38,0.1)]">
        <div>
          <h3 className="text-xl font-bold text-red-500 flex items-center gap-2 mb-1">
            🔥 The Watchtower Furnace
          </h3>
          <p className="text-sm text-red-200/70">
            Burn 1 <span className="text-red-300 font-semibold">Watchtower Yield Emblem</span> for +24 hours of +0.25% APR! <br className="hidden sm:block" />(Burns stack time automatically)
          </p>
        </div>
        <button
          onClick={handleBurnAprEmblem}
          disabled={playerData?.isBlacklisted}
          className="group relative px-6 py-3 font-bold text-white rounded-lg bg-red-600 hover:bg-red-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden border border-red-400"
        >
          <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]"></div>
          Burn Emblem
        </button>
      </div>

      {/* TOKEN SELECTOR & ADD STAKE */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 bg-black/50 backdrop-blur-md border border-zinc-800 rounded-xl p-6">
          <div className="flex flex-wrap gap-2 mb-6">
            {['WAX', 'WHATIF', 'FIREMAN', 'PUZZL', 'WTFWUF', 'WTFBILL', 'WHATIFX', 'TESTERU', 'XMASZS'].map(token => (
              <button
                key={token}
                onClick={() => handleSelectToken(token)}
                className={`flex flex-col items-center justify-center px-4 py-2 rounded-md font-bold text-sm transition-all border ${selectedVaultToken === token
                    ? 'bg-green-600/20 border-green-500 text-green-400 shadow-[0_0_10px_rgba(34,197,94,0.3)]'
                    : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-200'
                  }`}
              >
                <span>{token}</span>
                {VAULT_TOKEN_VALUE[token] && (
                  <span className="text-[10px] opacity-70 font-normal mt-0.5">
                    {VAULT_TOKEN_VALUE[token]} pts
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-white">Stake {selectedVaultToken}</h3>
            <div className="text-sm text-zinc-400">
              Balance: <span className="font-mono text-white">{tokenBalances[selectedVaultToken] || `0.0000 ${selectedVaultToken}`}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs uppercase text-zinc-500 mb-1">Amount</label>
              <input
                type="number"
                step={VAULT_TOKEN_INFO[selectedVaultToken]?.decimals ? `0.${'0'.repeat(VAULT_TOKEN_INFO[selectedVaultToken]?.decimals - 1)}1` : "1"}
                value={vaultStakeAmountInput}
                onChange={(e) => setVaultStakeAmountInput(e.target.value)}
                placeholder={`Min: ${VAULT_TOKEN_INFO[selectedVaultToken]?.min_stake}`}
                disabled={playerData?.isBlacklisted}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg p-3 text-white focus:outline-none focus:border-green-500 transition-colors font-mono"
              />
            </div>
            <div>
              <label className="block text-xs uppercase text-zinc-500 mb-1">Lock Tier</label>
              <select
                value={vaultLockPeriodInput}
                onChange={(e) => setVaultLockPeriodInput(e.target.value)}
                disabled={playerData?.isBlacklisted}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg p-3 text-white focus:outline-none focus:border-green-500 transition-colors font-mono"
              >
                {Object.entries(STAKING_TIERS).map(([key, value]) => {
                  const effectiveApr = getEffectiveApr(value.apr);
                  return (
                    <option key={key} value={key}>
                      {value.lockMonths === 0.5 ? 'Flexible' : `${value.lockMonths} Month(s)`} ({effectiveApr}% APR)
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          <button
            onClick={handleInitiateVaultStake}
            disabled={isInitiatingVaultStake || !playerData || playerData?.isBlacklisted}
            className="w-full py-3 rounded-lg font-bold text-black bg-green-500 hover:bg-green-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_15px_rgba(34,197,94,0.4)] hover:shadow-[0_0_25px_rgba(34,197,94,0.6)]"
          >
            {isInitiatingVaultStake ? 'Initiating...' : `Stake ${selectedVaultToken}`}
          </button>

          {parseFloat(vaultStakeAmountInput) > 0 && parseFloat(tokenBalances[selectedVaultToken]?.split(' ')[0] || 0) < parseFloat(vaultStakeAmountInput) && (
            <p className="text-red-400 text-sm mt-2 text-center">Insufficient {selectedVaultToken} balance.</p>
          )}
        </div>

        {/* Global Controls & Info */}
        <div className="bg-black/50 backdrop-blur-md border border-zinc-800 rounded-xl p-6 flex flex-col justify-between">
          <div>
            <h3 className="text-lg font-bold text-white mb-2">Vault Actions</h3>
            <p className="text-sm text-zinc-400 mb-6">Claim all your available rewards across all tokens in one transaction.</p>
            <button
              onClick={handleClaimAll}
              disabled={isClaimingAll || playerData?.isBlacklisted || isCooldownActive}
              className="w-full py-3 rounded-lg font-bold text-white bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-4"
            >
              {isClaimingAll ? 'Processing...' : isCooldownActive ? `Cooldown: ${claimCooldown}` : 'Claim All Rewards'}
            </button>
            {vaultStakingFeedback && (
              <div className={`p-3 rounded-lg text-sm ${vaultStakingFeedback.includes('❌') || vaultStakingFeedback.includes('Error') ? 'bg-red-900/30 text-red-400 border border-red-800' : vaultStakingFeedback.includes('⏳') ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-800' : 'bg-green-900/30 text-green-400 border border-green-800'}`}>
                {vaultStakingFeedback}
              </div>
            )}
            {(() => {
              const nowMs = Date.now();
              const unlockedCount = vaultStakes.filter(s => {
                if (s.tokenSymbol !== selectedVaultToken) return false;
                const unlockMs = s.unlockTime?.toMillis?.() ?? (s.unlockTime?.toDate?.().getTime()) ?? 0;
                return nowMs >= unlockMs && s.stakedAmount > 0;
              }).length;
              if (unlockedCount < 2) return null;
              return (
                <button
                  onClick={async () => {
                    setIsUnstakingAll(true);
                    await handleUnstakeAll(selectedVaultToken);
                    setIsUnstakingAll(false);
                  }}
                  disabled={isUnstakingAll || playerData?.isBlacklisted}
                  className="w-full py-3 rounded-lg font-bold text-white bg-red-900/60 hover:bg-red-800/80 border border-red-700/60 transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                >
                  {isUnstakingAll
                    ? '⏳ Processing...'
                    : `Unstake All Unlocked (${unlockedCount})`}
                </button>
              );
            })()}
          </div>

          {VAULT_TOKEN_VALUE[selectedVaultToken] && (
            <div className="mt-4 p-4 bg-blue-900/20 border border-blue-800/50 rounded-lg">
              <div className="text-xs text-blue-400 uppercase font-bold mb-1">Combat Boost</div>
              <div className="text-sm text-blue-200">
                1 {selectedVaultToken} = {VAULT_TOKEN_VALUE[selectedVaultToken]} Points. <br />
                Current Boost: <span className="font-bold text-white">+{totalBoosts}%</span>
              </div>
              {VAULT_TIERS[selectedVaultToken]?.tiers && (
                <div className="mt-2 text-xs text-blue-300 opacity-90">
                  <strong className="text-blue-400">Boost Tiers:</strong><br />
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
                    {VAULT_TIERS[selectedVaultToken].tiers.map((tier, index) => (
                      <span key={index} className={totalBoosts >= tier.boost ? "text-green-400 font-bold" : ""}>
                        {tier.amount.toLocaleString()} {selectedVaultToken} = +{tier.boost}%
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ACTIVE STAKES */}
      <h3 className="text-xl font-bold text-white mb-4">Your Active {selectedVaultToken} Stakes</h3>

      {vaultStakes.filter(s => s.tokenSymbol === selectedVaultToken).length === 0 ? (
        <div className="bg-black/30 border border-zinc-800 border-dashed rounded-xl p-10 text-center text-zinc-500">
          No active stakes for {selectedVaultToken}.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {vaultStakes.filter(s => s.tokenSymbol === selectedVaultToken).map(stake => {
            const unlockDate = stake.unlockTime?.toDate ? stake.unlockTime.toDate() : new Date(stake.unlockTime);
            const nowTime = new Date();
            const timeRemaining = unlockDate ? (unlockDate.getTime() - nowTime.getTime()) : 0;
            const daysRemaining = Math.max(0, Math.ceil(timeRemaining / (1000 * 60 * 60 * 24)));
            const isUnlocked = daysRemaining === 0;
            const decimals = VAULT_TOKEN_INFO[selectedVaultToken]?.decimals ?? 5;

            // Zero APR Shield
            const isZeroApr = stake.apr === 0 || stake.lockTier === 'flexible';
            const displayApr = isZeroApr ? 0 : stake.apr;
            const displayYield = isZeroApr ? 0 : (stake.accruedRewards || 0);

            return (
              <div key={stake.id} className="bg-black/60 backdrop-blur-md border border-zinc-800 rounded-xl overflow-hidden shadow-lg flex flex-col relative">

                {/* Header */}
                <div className="bg-zinc-900 p-3 border-b border-zinc-800 flex justify-between items-center">
                  <div className="font-mono font-bold text-green-400 text-lg">
                    {stake.stakedAmount?.toFixed(decimals)} <span className="text-xs text-zinc-500">{selectedVaultToken}</span>
                  </div>
                  <div className="text-xs font-bold px-2 py-1 bg-zinc-800 rounded text-zinc-300">
                    {stake.lockTier || 'N/A'}
                  </div>
                </div>

                {/* Body */}
                <div className="p-4 flex-grow flex flex-col gap-3">
                  <div className="flex justify-between items-end">
                    <div>
                      <div className="text-xs text-zinc-500 uppercase mb-1">Pending Rewards</div>
                      <div className="font-mono text-xl text-white">
                        {isZeroApr ? (
                          <span className="text-zinc-500">0.00000</span>
                        ) : (
                          displayYield.toFixed(decimals)
                        )}
                      </div>
                    </div>
                    {isZeroApr ? (
                      <div className="px-2 py-1 bg-blue-900/30 border border-blue-700/50 text-blue-400 text-xs rounded font-bold flex items-center gap-1">
                        🛡️ Secure Storage
                      </div>
                    ) : (
                      <div className="text-xs text-green-500 font-bold bg-green-900/20 px-2 py-1 rounded">
                        {getEffectiveApr(displayApr)}% Base APR
                      </div>
                    )}
                  </div>

                  {/* Progress / Time remaining */}
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-zinc-500 uppercase">Lock Status</span>
                      <span className={isUnlocked ? "text-green-400 font-bold" : "text-red-400 font-bold"}>
                        {isUnlocked ? 'Unlocked' : `${daysRemaining} days left`}
                      </span>
                    </div>
                    <div className="w-full bg-zinc-800 h-1.5 rounded-full overflow-hidden">
                      {isUnlocked ? (
                        <div className="bg-green-500 h-full w-full"></div>
                      ) : (
                        <div className="bg-red-500 h-full w-full opacity-50 animate-pulse"></div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="p-3 bg-zinc-900/50 border-t border-zinc-800 grid grid-cols-2 gap-2">
                  <button
                    onClick={() => handleClaimVaultRewards(stake.id)}
                    disabled={playerData?.isBlacklisted || displayYield < 0.00001 || isZeroApr}
                    className="py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-green-400 font-bold text-sm transition-colors border border-green-900/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:text-zinc-500 disabled:border-zinc-800"
                  >
                    Claim Rewards
                  </button>
                  <button
                    onClick={() => handleUnstakeVaultTokens(stake.id)}
                    disabled={!isUnlocked || playerData?.isBlacklisted}
                    className="py-2 rounded bg-zinc-800 hover:bg-red-900/40 text-red-400 font-bold text-sm transition-colors border border-red-900/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:text-zinc-500 disabled:border-zinc-800 flex items-center justify-center gap-1"
                  >
                    {!isUnlocked && <span>🔒</span>}
                    Unstake
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default VaultPage;